import { useEffect } from 'react'
import { desktop } from './desktop'
import { voiceOverlaySnapshot } from './voiceOverlay'
import { avatarUrl, memberAvatarUrl } from './avatar'
import { useVoiceStore } from '@/store/voice'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore, channelName } from '@/store/social'
import { useAuthStore } from '@/store/auth'

/** A small presentation snapshot, with no tokens, messages, or media tracks. */
export function useDesktopOverlay(): void {
  useEffect(() => {
    const bridge = desktop?.overlay
    if (!bridge) return
    let pending: ReturnType<typeof setTimeout> | undefined
    const send = () => {
      const voice = useVoiceStore.getState()
      const self = useAuthStore.getState().user
      const guilds = useGuildStore.getState()
      const social = useSocialStore.getState()
      const conn = voice.connection
      const gid = conn?.guildId
      const channel = conn ? gid ? guilds.channels[gid]?.[conn.channelId] : social.channels[conn.channelId] : undefined
      const states = (conn ? gid ? guilds.voiceStates[gid] : social.callStates[conn.channelId] : undefined) ?? {}
      const member = (id: string) => gid ? guilds.members[gid]?.[id] ?? states[id]?.member : undefined
      const user = (id: string) => id === self?.id ? self : states[id]?.member?.user ?? channel?.recipients?.find((u) => u.id === id) ?? social.relationships[id]?.user
      bridge.update(voiceOverlaySnapshot({
        connection: conn, self, channel: channel ? channelName(channel) : 'Разговор',
        remote: voice.remote, speaking: voice.speaking, selfMute: voice.selfMute, selfDeaf: voice.selfDeaf, live: voice.live, selfSharing: voice.sharing, states, member, user,
        avatar: (id) => { const m = member(id); return m ? memberAvatarUrl(gid, m) : avatarUrl(id, user(id)?.avatar) },
      }))
    }
    const schedule = () => {
      if (!useVoiceStore.getState().connection || !useAuthStore.getState().user) {
        clearTimeout(pending); pending = undefined; send(); return
      }
      if (pending !== undefined) return
      pending = setTimeout(() => { pending = undefined; send() }, 80)
    }
    const off = [
      useVoiceStore.subscribe((s, p) => {
        if (s.connection !== p.connection || s.remote !== p.remote || s.speaking !== p.speaking || s.selfMute !== p.selfMute || s.selfDeaf !== p.selfDeaf || s.live !== p.live || s.sharing !== p.sharing) schedule()
      }),
      useGuildStore.subscribe((s, p) => { if (s.channels !== p.channels || s.members !== p.members || s.voiceStates !== p.voiceStates) schedule() }),
      useSocialStore.subscribe((s, p) => { if (s.channels !== p.channels || s.callStates !== p.callStates || s.relationships !== p.relationships) schedule() }),
      useAuthStore.subscribe((s, p) => { if (s.user !== p.user) schedule() }),
    ]
    send()
    // If the page hangs or crashes, the native overlay expires instead of showing stale speakers.
    const heartbeat = setInterval(() => { if (useVoiceStore.getState().connection) send() }, 5000)
    return () => { clearTimeout(pending); clearInterval(heartbeat); off.forEach((stop) => stop()); bridge.update(null) }
  }, [])
}
